import { FlightSegment } from 'constants/flight'
import { getBudgetedSelectedDepartingLegPrice, getPendingReturningLegPrice } from 'checkout/selectors/view/flights'
import { applyFeederBudgetToCost } from 'lib/checkout/flights/cart'

export const FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE = 'single'
export const FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED = 'combined'

type BaseOptions = {
  flightItem?: App.Checkout.FlightItem
  fareFamilies: Array<App.FlightFareFamily>
  isReturnType: boolean
  /** Package total baseline (hotel + charter's real current price) each fare family's total is built on. */
  baseline: number
}

type SingleModalOptions = BaseOptions & {
  mode: typeof FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE
  departingJourney?: App.JourneyV2
}

type CombinedModalOptions = BaseOptions & {
  mode: typeof FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_COMBINED
  currentSegment: FlightSegment
  selectedDepartingFareFamily?: App.FlightFareFamily
  getOverrideTotalCost: (fareFamily: App.FlightFareFamily) => number
}

type Options = SingleModalOptions | CombinedModalOptions

function getFareFamilyTotalCost(fareFamily?: App.FlightFareFamily): number {
  if (!fareFamily) return 0
  return fareFamily.costWithInsurance?.all ?? fareFamily.cost.all
}

/**
 * Per fare family absolute package totals for the Departing modal: each fare family's own
 * budgeted contribution (reusing `getBudgetedSelectedDepartingLegPrice`, the exact function
 * the real checkout total uses) plus the pending (not yet chosen) returning leg's placeholder.
 * Every option is a self-contained, honest total - not a delta relative to the cheapest tile.
 */
function getDepartingPageLabels(
  flightItem: App.Checkout.FlightItem,
  fareFamilies: Array<App.FlightFareFamily>,
  optionFareFor: (fareFamily: App.FlightFareFamily) => number,
  baseline: number,
): Map<string, number> {
  const pendingReturningLegPrice = getPendingReturningLegPrice(flightItem)
  const map = new Map<string, number>()
  fareFamilies.forEach(fareFamily => {
    const departingContribution = getBudgetedSelectedDepartingLegPrice(flightItem, optionFareFor(fareFamily))
    map.set(fareFamily.id, baseline + departingContribution + pendingReturningLegPrice)
  })
  return map
}

/**
 * Per fare family absolute package totals for the Returning modal: the already-selected
 * departing leg's budgeted contribution (reusing the same function the departing page
 * used for it) plus each fare family's own budgeted returning contribution (`applyFeederBudgetToCost`,
 * the exact function that sets the real cart cost at selection time).
 */
function getReturningPageLabels(
  flightItem: App.Checkout.FlightItem,
  fareFamilies: Array<App.FlightFareFamily>,
  optionFareFor: (fareFamily: App.FlightFareFamily) => number,
  selectedDepartingFare: number,
  baseline: number,
): Map<string, number> {
  const departingContribution = getBudgetedSelectedDepartingLegPrice(flightItem, selectedDepartingFare)
  const map = new Map<string, number>()
  fareFamilies.forEach(fareFamily => {
    const optionFare = optionFareFor(fareFamily)
    const returningContribution = flightItem.bundleBudget
      ? applyFeederBudgetToCost(optionFare, flightItem.bundleBudget, 'returning')
      : optionFare
    map.set(fareFamily.id, baseline + departingContribution + returningContribution)
  })
  return map
}

function getSingleModalFeederBudgetLabels({
  flightItem,
  fareFamilies,
  departingJourney,
  baseline,
}: SingleModalOptions & { flightItem: App.Checkout.FlightItem }): Map<string, number> {
  const optionFareFor = (ff: App.FlightFareFamily): number => getFareFamilyTotalCost(ff)

  if (departingJourney) {
    // Returning page modal: the departing leg is already selected.
    const selectedDepartingFare = departingJourney.price?.all?.totalFare ?? 0
    return getReturningPageLabels(flightItem, fareFamilies, optionFareFor, selectedDepartingFare, baseline)
  }

  return getDepartingPageLabels(flightItem, fareFamilies, optionFareFor, baseline)
}

function getCombinedModalFeederBudgetLabels({
  flightItem,
  fareFamilies,
  currentSegment,
  selectedDepartingFareFamily,
  getOverrideTotalCost,
  isReturnType,
  baseline,
}: CombinedModalOptions & { flightItem: App.Checkout.FlightItem }): Map<string, number> {
  const isReturningSegment = currentSegment === FlightSegment.RETURNING
  // Departing tab on return-type bookings uses merged dep+ret override totals;
  // every other case uses the fare family's standalone leg fare.
  const optionFareFor = (fareFamily: App.FlightFareFamily): number => {
    if (!isReturningSegment && isReturnType) return getOverrideTotalCost(fareFamily)
    return getFareFamilyTotalCost(fareFamily)
  }

  if (isReturningSegment) {
    const selectedDepartingFare = getFareFamilyTotalCost(selectedDepartingFareFamily)
    return getReturningPageLabels(flightItem, fareFamilies, optionFareFor, selectedDepartingFare, baseline)
  }

  return getDepartingPageLabels(flightItem, fareFamilies, optionFareFor, baseline)
}

export default function getFareFamilyFeederBudgetLabels(options: Options): Map<string, number> | undefined {
  if (!options.flightItem) return undefined

  if (options.mode === FARE_FAMILY_FEEDER_BUDGET_LABEL_MODE_SINGLE) {
    return getSingleModalFeederBudgetLabels({
      ...options,
      flightItem: options.flightItem,
    })
  }

  return getCombinedModalFeederBudgetLabels({
    ...options,
    flightItem: options.flightItem,
  })
}
